import {AttrSchema, Component, ConfigOptions} from 'panel';
import isEqual from 'lodash/isEqual';

import {defineAndMakeJsxFactory} from 'common/util/jsx';

import {docs} from '../../docs';
import {ComponentSchema} from '../../docs/types';
import {template} from './template';
import './index.styl';

const ATTR_TYPE_DEFAULTS = {
  string: ``,
  boolean: false,
  number: 0,
  json: null,
};

const ICON_FOR_TYPE = {
  string: `type-text`,
  boolean: `type-boolean`,
  number: `type-number`,
  json: `type-object`,
};

interface AttrRow {
  attr: string;
  value: any;
  type: string;
  default: any;
  description?: string;
  enum?: string[];
  deprecatedMsg?: string;
  tsType?: string;
  hasError?: boolean;
}

interface EventRow {
  event: string;
  when?: string;
  detail?: string;
  isLogging: boolean;
  logListener: (ev: Event) => void;
  logCount: number;
}

interface AutoDocState {
  attrRows: AttrRow[];
  eventRows: EventRow[];
  isExpanded: boolean;
}

export class AutoDoc extends Component<AutoDocState> {
  static get attrsSchema(): Record<string, AttrSchema | string> {
    return {
      component: `string`,
    };
  }

  get config(): ConfigOptions<AutoDocState> {
    return {
      template,
      defaultState: {
        attrRows: [],
        eventRows: [],
        isExpanded: false,
      },
      helpers: {
        ICON_FOR_TYPE,

        isValueChanged: (row): boolean => !isEqual(row.value, row.default),

        areAllEventsLogged: (): boolean => this.state.eventRows.every((r) => r.isLogging),

        handleValueChanged: (row, value): void => {
          if (row.type === `json`) {
            // handle json as a special case where we parse the input
            try {
              row.value = JSON.parse(value);
              row.hasError = false;
            } catch (err) {
              row.hasError = true;
            }
          } else {
            row.value = value;
          }

          this.update();
          const elem: Element = this.closest(`.auto-doc-wrapper`).querySelector(this.helpers.getComponentSelector());
          if (elem) {
            value = row.type === `json` ? JSON.stringify(row.value) : row.value;

            if (this.helpers.isValueChanged(row)) {
              elem.setAttribute(row.attr, value);
            } else {
              elem.removeAttribute(row.attr);
            }

            const codeTip = elem.closest(`code-tip.example`);
            codeTip.classList.add(`autodoc-customized`);
          }
        },

        handleLoggingChanged: (row, isLogging): void => {
          const ev = row.event;
          row.isLogging = isLogging;
          row.logCount = 0;
          row.logListener = this.helpers.logEvent.bind(this, row);

          this.update();
          const elems: NodeListOf<Element> = this.closest(`.sub-section`).querySelectorAll(
            this.helpers.getComponentSelector(),
          );

          for (const elem of Array.from(elems)) {
            if (isLogging) {
              elem.addEventListener(ev, row.logListener);
            } else {
              elem.removeEventListener(ev, row.logListener);
            }
          }
        },

        handleCollapsibleChange: (ev): void => {
          // make sure it's a change event from mp-collapisble and not a bubbled event from a checkbox
          if (ev.target.nodeName.toLowerCase() === `mp-section`) {
            this.update({isExpanded: ev.detail.state === `open`});
          }
        },

        toggleAllEventsLogging: (): void => {
          const isLogging = !this.helpers.areAllEventsLogged();
          for (const row of this.state.eventRows) {
            this.helpers.handleLoggingChanged(row, isLogging);
          }
        },

        logEvent: (row, ev): void => {
          console.log(ev.target.localName, ev.type, ev.detail, `\n`, ev); // eslint-disable-line no-console
          row.logCount++;
          this.update();
        },

        getComponentSelector: (): string => `code-tip.example ${this.attr(`component`)}`,
      },
    };
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.updateAttrRows();
    this.updateEventRows();
  }

  updateAttrRows(): void {
    const component = customElements.get(this.attr(`component`));
    if (!component) {
      customElements.whenDefined(this.attr(`component`)).then(() => this.updateAttrRows());
      return;
    }

    const attrsSchema = this.getComponentSchema().attrs;
    const attrRows = Object.entries(attrsSchema)
      .map(([attr, schema]) => {
        if (typeof schema === `string`) {
          schema = {type: schema};
        }
        const row = {
          attr,
          default: ATTR_TYPE_DEFAULTS[schema.type],
          description: ``,
          hasError: false,
          ...schema,
          value: null,
        };

        row.value = row.default;
        return row;
      })
      .sort((a, b) => a.attr.localeCompare(b.attr));

    this.update({attrRows});
  }

  updateEventRows(): void {
    const eventsSchema = this.getComponentSchema().events;
    if (eventsSchema) {
      const eventRows = Object.entries(eventsSchema)
        .map(([event, schema]) => ({
          event,
          ...schema,
          isLogging: false,
          logListener: null,
          logCount: 0,
        }))
        .sort((a, b) => a.event.localeCompare(b.event));

      this.update({eventRows});
    }
  }

  /**
   * Merges docs schema and attrsSchema of an element
   */
  getComponentSchema(): ComponentSchema {
    const componentName = this.attr(`component`);
    const customEl = customElements.get(componentName);
    const attrsSchema: {[attr: string]: AttrSchema} = customEl.attrsSchema;
    const componentDocSchema = docs[componentName] || {};
    if (attrsSchema) {
      componentDocSchema.attrs = {...componentDocSchema.attrs, ...attrsSchema};
    }
    return componentDocSchema;
  }
}

export const AutoDocEl = defineAndMakeJsxFactory(`auto-doc`, AutoDoc);
